import { useEffect, useState, useRef } from 'react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

interface OfflineIndicatorProps {
  hideWhenPlaying?: boolean;
}

/**
 * Displays offline mode indicator and reconnection toast.
 * Automatically shows/hides based on network status.
 */
export default function OfflineIndicator({ hideWhenPlaying = false }: OfflineIndicatorProps) {
  const isOnline = useOnlineStatus();
  const [showToast, setShowToast] = useState(false);
  const prevOnline = useRef(isOnline);

  useEffect(() => {
    if (!prevOnline.current && isOnline) {
      setShowToast(true);
      const timerId = setTimeout(() => setShowToast(false), 3000);
      return () => clearTimeout(timerId);
    }
    prevOnline.current = isOnline;
  }, [isOnline]);

  return (
    <>
      {!isOnline && !hideWhenPlaying && (
        <div
          data-testid="offline-indicator"
          className="fixed top-4 right-4 z-50 transition-opacity duration-300"
          role="status"
          aria-live="polite"
        >
          <div className="bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <span className="text-lg" aria-hidden="true">⚠️</span>
            <span className="font-medium">Offline Mode</span>
          </div>
        </div>
      )}

      {showToast && (
        <div
          data-testid="reconnection-toast"
          className="fixed top-4 right-4 z-50 animate-slide-down"
          role="alert"
          aria-live="assertive"
        >
          <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <span className="text-lg" aria-hidden="true">✓</span>
            <span className="font-medium">You&apos;re back online! Syncing progress...</span>
          </div>
        </div>
      )}
    </>
  );
}
